import { FC, useEffect, useRef, useState } from 'react';
import { SwitchButton } from '../components/SwitchButton/SwitchButton';
import { ChangeDescriptionInput } from '../components/ChangeDescriptionInput/ChangeDescriptionInput';
import { ChangeLimitInput } from '../components/ChangeLimitInput/ChangeLimitInput';
import { ActionButtons } from '../components/ActionButtons/ActionButtons';
import { type CardsModel } from '../../../../../../../utils/types';
import {
  useApplyCardsSettingsMutation,
  useGetCardsQuery,
  useResetCardIncomeMutation,
  useToggleAllCardsMutation,
} from '../../../../../../../redux/services/CardsApi';
import mapCards from '../../../../../../../utils/functions/map/mapCards';
import { Loading } from '../../../../../../../components/Loading/Loading';
import { cardThArray } from '../../../../../../../utils/constants/th_arrays';
import { Pagination } from '../../../../../../../components/Pagination/Pagination';
import { type TCardsContentProps } from '../../CardsContent';
import { PaginationStyles, StyledTable, TableWrapperStyles } from './styles';
import { ChangeIncomeLimitInput } from '../components/ChangeIncomeLimitInput/ChangeIncomeLimitInput';
import { pushNotify } from 'components/Notifications';
import { useFormContext } from 'react-hook-form';
import { useColumnVisibility } from 'components/Table/useColumnVisibility';
import { parseStringToNumber } from '../../../../../../../utils/functions/parseNumber';

const PeriodLimitControl = ({
  enabledName,
  limitName,
  enabled,
  limit,
  isEditing,
}: {
  enabledName: 'newDailyLimitEnabled' | 'newMonthlyLimitEnabled';
  limitName: 'newDailyLimit' | 'newMonthlyLimit';
  enabled: boolean;
  limit: string;
  isEditing: boolean;
}) => {
  const { register } = useFormContext();

  if (!isEditing) {
    return <>{enabled ? limit || '0' : 'Выкл'}</>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
      <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px' }}>
        <input type='checkbox' {...register(enabledName)} defaultChecked={enabled} />
        Активен
      </label>
      <ChangeLimitInput
        id={1}
        currentId={1}
        defaultValue={limit}
        name={limitName}
        placeholder='0'
        rules={{ maxLength: 13 }}
      />
    </div>
  );
};

export const CardsTable: FC<TCardsContentProps> = ({
  currentId,
  setCurrentId,
  page,
  setPage,
  pageSize,
  search,
  currentPageIds,
  setCurrentPageIds,
}) => {
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const { data, isLoading } = useGetCardsQuery({ page, page_size: pageSize, search });
  const [toggleAllCards, { isLoading: isToggleLoading }] = useToggleAllCardsMutation();
  const [resetCardIncome] = useResetCardIncomeMutation();
  const [applyCardsSettings, { isLoading: isApplyLoading }] = useApplyCardsSettingsMutation();
  const { getValues, setValue } = useFormContext();
  const {
    isOpen,
    setIsOpen,
    pickerRef,
    visibleColumns,
    visibleCount,
    filteredThArray,
    filterRow,
    toggleColumn,
  } = useColumnVisibility('merchant-cards', cardThArray);
  const hasCards = Boolean(data?.total_count ?? data?.count ?? 0);
  const isActionsLoading = isToggleLoading || isApplyLoading;

  useEffect(() => {
    const ids = (data?.results || []).map((card) => card.id);
    setCurrentPageIds(ids);
  }, [data, setCurrentPageIds]);

  useEffect(() => {
    const currentCard = mapCards(data?.results)?.find((card) => card.id === currentId);
    if (!currentCard) {
      return;
    }
    setValue('newDescription', currentCard.description);
    setValue('newLimit', String(currentCard.total_limit));
    setValue('newMinIncome', Number(currentCard.min_income));
    setValue('newMaxIncome', Number(currentCard.max_income));
    setValue('newInstantOrders', String(currentCard.instant_active_orders));
    setValue('newDailyLimit', String(currentCard.daily_total_limit));
    setValue('newMonthlyLimit', String(currentCard.monthly_total_limit));
    setValue('newDailyLimitEnabled', currentCard.daily_limit_enabled);
    setValue('newMonthlyLimitEnabled', currentCard.monthly_limit_enabled);
  }, [currentId, data, setValue]);

  const handleTogglePage = async (active: boolean) => {
    if (currentPageIds.length === 0) {
      return;
    }
    await toggleAllCards({ active, ids: currentPageIds })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось изменить статус реквизитов', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  const handleResetIncome = async (cardId: number) => {
    await resetCardIncome(cardId)
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось обнулить пополнения', 'Ошибка');
      });
  };

  const handleApplySettingsAll = async () => {
    if (!currentId) {
      return;
    }

    const selectedCard = data?.results.find((card) => card.id === currentId);
    if (!selectedCard?.currency_code) {
      pushNotify('Не удалось определить валюту выбранного реквизита', 'Ошибка');
      return;
    }

    const values = getValues();
    await applyCardsSettings({
      currency_code: selectedCard.currency_code,
      total_limit: parseStringToNumber(values.newLimit),
      min_income: +values.newMinIncome,
      max_income: +values.newMaxIncome,
      instant_active_orders: parseStringToNumber(values.newInstantOrders),
      daily_total_limit: parseStringToNumber(values.newDailyLimit || '0'),
      monthly_total_limit: parseStringToNumber(values.newMonthlyLimit || '0'),
      daily_limit_enabled: !!values.newDailyLimitEnabled,
      monthly_limit_enabled: !!values.newMonthlyLimitEnabled,
    })
      .unwrap()
      .catch((error: any) => {
        pushNotify(error?.data?.detail || 'Не удалось применить настройки ко всем реквизитам', 'Ошибка');
      });
    setIsActionsOpen(false);
  };

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!actionsMenuRef.current?.contains(event.target as Node)) {
        setIsActionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  return isLoading ? (
    <Loading />
  ) : (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            flex: '1 1 auto',
          }}
        >
          <div style={{ padding: '10px 12px', border: '1px solid #e2e5f0', borderRadius: '6px' }}>
            Всего: {data?.total_count || 0}
          </div>
          <div style={{ padding: '10px 12px', border: '1px solid #e2e5f0', borderRadius: '6px' }}>
            Включено: {data?.active_count || 0}
          </div>
          <div style={{ padding: '10px 12px', border: '1px solid #e2e5f0', borderRadius: '6px' }}>
            Выключено: {data?.inactive_count || 0}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }} ref={pickerRef}>
            <button
              type='button'
              onClick={() => setIsOpen(!isOpen)}
              style={{ height: '36px', padding: '0 12px' }}
            >
              Столбцы
            </button>
            {isOpen && (
              <div className='table_columns_dropdown'>
                {cardThArray.map((th, index) => (
                  <label key={`${th.name}-${index}`} className='table_columns_option'>
                    <input
                      type='checkbox'
                      checked={visibleColumns[index] ?? true}
                      disabled={(visibleColumns[index] ?? true) && visibleCount === 1}
                      onChange={() => toggleColumn(index)}
                    />
                    <span>{th.name || `Столбец ${index + 1}`}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className='cards_actions' ref={actionsMenuRef}>
            <button
              type='button'
              className='cards_actions_button'
              disabled={isActionsLoading}
              onClick={() => setIsActionsOpen(!isActionsOpen)}
            >
              Действия
            </button>
            {isActionsOpen && (
              <div className='cards_actions_menu'>
                <div className='cards_actions_group'>Страница</div>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!currentPageIds.length || isActionsLoading}
                  onClick={() => handleTogglePage(true)}
                >
                  Включить страницу
                </button>
                <button
                  type='button'
                  className='cards_actions_item'
                  disabled={!currentPageIds.length || isActionsLoading}
                  onClick={() => handleTogglePage(false)}
                >
                  Выключить страницу
                </button>
                <div className='cards_actions_group'>Редактирование</div>
                <button
                  type='button'
                  className='cards_actions_item accent'
                  disabled={!currentId || !hasCards || isActionsLoading}
                  onClick={handleApplySettingsAll}
                >
                  Применить настройки ко всем
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <TableWrapperStyles>
        <StyledTable>
          <thead>
            <tr>
              {filteredThArray.map((th, index) => (
                <th
                  key={index}
                  style={{
                    ...th.style,
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    maxWidth: '250px',
                    backgroundColor: '#e2e5f0',
                    fontWeight: '400',
                  }}
                >
                  {th.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mapCards(data?.results)?.map((card: CardsModel) => {
              const row = [
                {
                  element: <SwitchButton active={card.active} id={card.id} />,
                },
                {
                  element: card.tag,
                },
                {
                  element: (
                    <ChangeDescriptionInput
                      id={card.id}
                      currentId={currentId}
                      defaultValue={card.description}
                      placeholder={''}
                      name={'newDescription'}
                    />
                  ),
                },
                { element: card.payment_type },
                {
                  element:
                    card.payment_type === 'C2C' || card.payment_type === 'SBP' ? card.account_number : <>-</>,
                },
                { element: card.payment_type === 'TRANSFER' ? card.account_number : <>-</> },
                { element: card.bank.bic ?? <>-</> },
                { element: card.bank.name },
                { element: card.payment_type === 'TRANSFER' ? 'Перевод' : '-' },
                { element: card.phone_number },
                {
                  element: (
                    <ChangeLimitInput
                      currentId={currentId}
                      id={card.id}
                      defaultValue={card.total_limit}
                      name='newLimit'
                      placeholder={'Лимит карты'}
                    />
                  ),
                },
                { element: card.fio },
                { element: card.total_income },
                {
                  element: (
                    <button
                      type='button'
                      style={{ padding: '6px 10px' }}
                      onClick={() => handleResetIncome(card.id)}
                    >
                      Обнулить
                    </button>
                  ),
                },
                {
                  element: (
                    <ChangeIncomeLimitInput
                      currentId={currentId}
                      id={card.id}
                      defaultValue={String(card.min_income)}
                      name='newMinIncome'
                      placeholder={''}
                    />
                  ),
                },
                {
                  element: (
                    <ChangeIncomeLimitInput
                      currentId={currentId}
                      id={card.id}
                      defaultValue={String(card.max_income)}
                      name='newMaxIncome'
                      placeholder={''}
                    />
                  ),
                },
                {
                  element: (
                    <ChangeLimitInput
                      currentId={currentId}
                      id={card.id}
                      defaultValue={String(card.instant_active_orders)}
                      name='newInstantOrders'
                      placeholder={''}
                      rules={{ min: 1, max: 100000000, maxLength: 11 }}
                    />
                  ),
                },
                {
                  element: (
                    <PeriodLimitControl
                      enabledName='newDailyLimitEnabled'
                      limitName='newDailyLimit'
                      enabled={card.daily_limit_enabled}
                      limit={String(card.daily_total_limit)}
                      isEditing={card.id === currentId}
                    />
                  ),
                },
                {
                  element: (
                    <PeriodLimitControl
                      enabledName='newMonthlyLimitEnabled'
                      limitName='newMonthlyLimit'
                      enabled={card.monthly_limit_enabled}
                      limit={String(card.monthly_total_limit)}
                      isEditing={card.id === currentId}
                    />
                  ),
                },
                {
                  element: <ActionButtons currentId={currentId} setCurrentId={setCurrentId} id={card.id} />,
                },
              ];

              return (
                <tr key={card.id}>
                  {filterRow(row).map((cell, index) => (
                    <td key={index}>{cell.element}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </StyledTable>
      </TableWrapperStyles>
      <PaginationStyles>
        <Pagination
          totalPages={Math.ceil((data?.count || 0) / pageSize)}
          setCurrentPage={setPage}
          currentPage={page}
          nextPage={typeof data?.next === 'number' ? data.next : page}
          previousPage={typeof data?.previous === 'number' ? data.previous : page}
        />
      </PaginationStyles>
    </>
  );
};
